import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const JourneyTimeline = () => {
  const [activePhase, setActivePhase] = useState(0);

  const journeyPhases = [
    {
      id: 0,
      phase: "Secondary Education",
      year: "2018-2019",
      title: "Secondary Education",
      description: "Completed my secondary education with a strong academic record, building a solid groundwork for my engineering studies.",
      achievements: [
        "Score 95.6% in CBSE",
        "Got 10th rank in the entire district",
        "Got Scholarship from the school"
      ],
      icon: "GraduationCap",
      color: "trust-green",
      bgColor: "trust-green/10"
    },
    {
      id: 1,
      phase: "Intermediate Education",
      year: "2020-2021",
      title: "Intermediate Education",
      description: "Completed my senior secondary education, building a strong academic and foundational skill set.",
      achievements: [
        "Score 95.83% in CBSE",
        "Got 10th rank in the entire district",
        "Got Scholarship from the school"
      ],
      icon: "Briefcase",
      color: "electric-cyan",
      bgColor: "electric-cyan/10"
    },
    {
      id: 2,
      phase: "Graduation",
      year: "2022-2023",
      title: "Explore Tech World",
      description: "Began my B.Tech degree, where I built a strong foundation in core computer science and simultaneously explored a range of modern technologies in my first year.",
      achievements: [
        "Score 8+ average CGPA",
        "Solved 150+ leetcode problems",
        "Learned Web Technologies & Tools"
      ],
      icon: "Lightbulb",
      color: "rocket-fire",
      bgColor: "rocket-fire/10"
    },
    {
      id: 3,
      phase: "Innovation",
      year: "2023+",
      title: "Career Trajectory",
      description:
        "Ready to launch into a professional career, bringing fresh perspectives and proven technical capabilities.",
      achievements: [
        "Build Real World Project in MERN Stack",
        "Deployed the projects",
        "Use the Git and Github for code management",
      ],
      icon: "Rocket",
      color: "redi",
      bgColor: "redi/10",
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-deep-space via-slate-900 to-slate-800 bg-background">
      <div id="TransformAndTimeline" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Icon name="Map" size={24} className="text-rocket-fire" />
            <span className="text-rocket-fire font-medium">Journey Map</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-headline text-deep-space mb-4 text-white">
            Transformation Timeline
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto text-white">
            From curious student to launch-ready professional - every milestone tells a story of growth, learning, and impact.
          </p>
        </div>

        {/* Desktop Timeline */}
        <div className="hidden lg:block">
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-trust-green via-electric-cyan via-rocket-fire to-deep-space transform -translate-y-1/2"></div>

            {/* Timeline Points */}
            <div className="relative flex justify-between items-center">
              {journeyPhases?.map((phase, index) => (
                <div key={phase?.id} className="flex flex-col items-center">
                  <button
                    onClick={() => setActivePhase(index)}
                    className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 mb-8 ${activePhase === index
                      ? `bg-${phase?.color} text-white shadow-lg scale-110`
                      : 'bg-white border-2 border-slate-200 text-slate-400 hover:border-slate-300'
                      }`}
                  >
                    <Icon name={phase?.icon} size={24} />
                  </button>

                  <div className={`text-center transition-all duration-300 ${activePhase === index ? 'opacity-100' : 'opacity-60'
                    }`}>
                    <div className={`text-sm font-medium mb-1 ${activePhase === index? `text-${phase?.color}`: 'text-slate-500'}`}>{phase?.year}</div>
                    <div className={`text-lg font-semibold ${activePhase === index? `text-${phase?.color}` : 'text-slate-400'}`}>{phase?.phase}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Active Phase Details */}
          <div className="mt-16">
            <div className={`bg-${journeyPhases?.[activePhase]?.bgColor} rounded-2xl p-8 transition-all duration-500`}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className={`text-2xl font-bold text-deep-space mb-4 text-${journeyPhases?.[activePhase]?.color}`}>
                    {journeyPhases?.[activePhase]?.title}
                  </h3>
                  <p className="text-text-secondary text-lg mb-6 text-white">
                    {journeyPhases?.[activePhase]?.description}
                  </p>
                  <div className="space-y-3 text-white">
                    {journeyPhases?.[activePhase]?.achievements?.map((achievement, idx) => (
                      <div key={idx} className="flex items-start space-x-3 text-sky-500">
                        <Icon name="CheckCircle" size={16} className={`text-${journeyPhases?.[activePhase]?.color} mt-1 flex-shrink-0 `} />
                        <span className="text-lime-500">{achievement}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-center">
                  <div className={`w-32 h-32 bg-${journeyPhases?.[activePhase]?.color}/20 rounded-full flex items-center justify-center`}>
                    <Icon name={journeyPhases?.[activePhase]?.icon} size={48} className={`text-${journeyPhases?.[activePhase]?.color}`} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Timeline */}
        <div className="lg:hidden space-y-8">
          {journeyPhases?.map((phase, index) => (
            <div key={phase?.id} className="relative">
              {index < journeyPhases?.length - 1 && (
                <div className="absolute left-8 top-16 bottom-0 w-0.5 bg-slate-200"></div>
              )}

              <div className="flex space-x-4">
                <div className={`w-16 h-16 bg-${phase?.color} rounded-full flex items-center justify-center flex-shrink-0`}>
                  <Icon name={phase?.icon} size={24} className="text-white" />
                </div>

                <div className="flex-1 pb-8">
                  <div className={`text-sm font-medium text-slate-500 mb-1 `}>{phase?.year}</div>
                  <h3 className={`text-xl font-bold text-deep-space mb-2 text-${phase.color}`}>{phase?.title}</h3>
                  <p className="text-text-secondary mb-4 text-white">{phase?.description}</p>

                  <div className="space-y-2">
                    {phase?.achievements?.map((achievement, idx) => (
                      <div key={idx} className="flex items-start space-x-2">
                        <Icon name="CheckCircle" size={14} className={`text-${phase?.color} mt-1 flex-shrink-0`} />
                        <span className="text-lime-200 text-sm ">{achievement}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default JourneyTimeline;